/**
 * useUserRuns Hook
 * 
 * Fetches and manages all pipeline runs for the current user.
 * Automatically connects to SSE streams for active runs.
 */

import { useState, useEffect, useCallback } from 'react';
import progressService from '../services/progressService';
import { useProgressStream } from './useProgressStream';
import { transformTaskForList, transformProgressData } from '../utils/progressDataTransformer';

export const useUserRuns = (userEmail, options = {}) => {
  const {
    autoRefresh = true, // Automatically refresh task list
    refreshInterval = 30000, // Refresh every 30 seconds
    connectToActiveStreams = true // Connect SSE to active runs
  } = options;

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runProgress, setRunProgress] = useState({}); // { runId: progressData }
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load user runs
  const loadRuns = useCallback(async (silent = false) => {
    if (!userEmail) {
      console.warn('⚠️ useUserRuns: No user email provided');
      setLoading(false);
      setError('User email not available. Please log in again.');
      return;
    }

    try {
      // Only show loading spinner on initial load, not on background refreshes
      if (!silent && isInitialLoad) {
        setLoading(true);
      }
      // Don't clear error on silent refreshes (keep showing it if it exists)
      if (!silent) {
        setError(null);
      }
      
      console.log(`📊 Loading runs for user: ${userEmail}`);
      const tasks = await progressService.getUserTasks(userEmail);
      console.log(`✅ Loaded ${tasks.length} tasks for user`);
      console.log(`📦 Raw tasks data:`, tasks);
      
      // Transform tasks to list format
      const transformedRuns = tasks.map(task => {
        const transformed = transformTaskForList(task);
        console.log(`🔄 Transformed task:`, transformed);
        return transformed;
      });
      
      // Sort by last_updated (newest first)
      transformedRuns.sort((a, b) => {
        const timeA = a.last_updated?.getTime() || 0;
        const timeB = b.last_updated?.getTime() || 0;
        return timeB - timeA;
      });
      
      setRuns(transformedRuns);

      // Load initial progress for each run
      const progressPromises = transformedRuns.map(async (run) => {
        try {
          const progress = await progressService.getAggregateProgress(run.run_id);
          return { runId: run.run_id, progress };
        } catch (err) {
          console.error(`Error loading progress for run ${run.run_id}:`, err);
          return null;
        }
      });

      const progressResults = await Promise.all(progressPromises);
      const progressMap = {};
      progressResults.forEach(result => {
        if (result) {
          progressMap[result.runId] = result.progress;
        }
      });
      setRunProgress(progressMap);
      
      // Mark initial load as complete
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }

    } catch (err) {
      console.error('❌ Error loading user runs:', err);
      const errorMessage = err.response?.status === 404 
        ? 'Progress tracking endpoint not found. Please ensure the backend service is running and progress routes are registered.'
        : err.response?.status === 401
        ? 'Authentication required. Please log in again.'
        : err.message || 'Failed to load pipeline runs';
      
      // Only show error on initial load or manual refresh, not on silent background refreshes
      if (!silent) {
        setError(errorMessage);
        setRuns([]); // Set empty array on error
      } else {
        // On silent refresh errors, just log them but don't disrupt the UI
        console.warn('⚠️ Silent refresh failed (will retry on next interval):', errorMessage);
      }
    } finally {
      // Mark initial load as complete after first load attempt
      if (isInitialLoad) {
        setIsInitialLoad(false);
        // Always set loading to false after initial load completes
        setLoading(false);
      }
      // For silent refreshes, don't touch the loading state at all
    }
  }, [userEmail, isInitialLoad]);

  // Initial load
  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Auto-refresh - more frequent for active runs
  useEffect(() => {
    if (!autoRefresh || !userEmail) return;

    // Refresh more frequently (every 5 seconds) if there are active runs
    const hasActiveRuns = runs.some(run => 
      run.status === 'processing' || run.status === 'running'
    );
    const refreshRate = hasActiveRuns ? 5000 : refreshInterval; // 5 seconds for active, 30 seconds otherwise

    const interval = setInterval(() => {
      // Use silent refresh for background updates (no loading spinner)
      loadRuns(true);
    }, refreshRate);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, userEmail, loadRuns, runs]);

  // Get transformed progress data for a specific run
  const getRunProgressData = useCallback((runId) => {
    const progress = runProgress[runId];
    const run = runs.find(r => r.run_id === runId);
    
    if (!progress && !run) return null;
    
    return transformProgressData(progress || {}, {
      run_id: run?.run_id,
      project_id: run?.project_id,
      project_name: run?.name,
      created_at: run?.created_at,
      user_email: run?.user_email,
      input_files: []
    });
  }, [runs, runProgress]);

  // Update progress for a specific run
  const updateRunProgress = useCallback((runId, progressData) => {
    setRunProgress(prev => ({
      ...prev,
      [runId]: progressData
    }));
  }, []);

  return {
    runs,
    loading,
    error,
    refresh: loadRuns,
    getRunProgressData,
    updateRunProgress,
    runProgress
  };
};

/**
 * Hook to manage SSE connection for a single run
 */
export const useRunProgressStream = (runId, enabled = true) => {
  const { progress, error, isConnected } = useProgressStream(runId, {
    enabled: enabled && !!runId,
    onComplete: (completedProgress) => {
      console.log(`Run ${runId} completed:`, completedProgress);
    }
  });

  return {
    progress,
    error,
    isConnected,
    progressData: progress ? transformProgressData(progress, { run_id: runId }) : null
  };
};

